import { type ComponentProps, ReactNode } from 'react';

import Dropdown, { GenericItem } from '@adsk/alloy-react-dropdown';

import { ReferencePickerPlugin } from '../../../index';

export interface ActivityResponse {
  id: number;
  scheduleId: string;
  scheduleName: string;
  sequentialId: number;
  uniqueId: number;
  parentId: number;
  isWbs: boolean;
  wbsCode: string;
  type: string;
  name: string | null;
  isCriticalPath: boolean;
  completionPercentage: number | null;
  plannedStart: string | null;
  plannedFinish: string | null;
  actualStart: string | null;
  actualFinish: string | null;
  durationDays: number | null;
  actualDurationDays: number | null;
  remainingDurationDays: number | null;
  freeSlackDays: number | null;
  totalSlackDays: number | null;
  start: string | null;
  finish: string | null;
  assignees: { name: string; type: string }[];
  activityCodes: ActivityCode[];
  wbsPath: number[];
  permittedActions?: string[];
}

interface ScheduleActivityEntityExtras {
  wbsPathActivities: Pick<ActivityResponse, 'name'>[];
  expanded?: boolean;
  disabled?: boolean;
  subRows?: ActivityRow[];
}

export type ScheduleActivityEntity = ScheduleActivityEntityExtras &
  Pick<
    ActivityResponse,
    | 'id'
    | 'uniqueId'
    | 'sequentialId'
    | 'name'
    | 'scheduleId'
    | 'isCriticalPath'
    | 'actualStart'
    | 'start'
    | 'plannedStart'
    | 'actualFinish'
    | 'finish'
    | 'plannedFinish'
    | 'assignees'
    | 'completionPercentage'
    | 'type'
    | 'durationDays'
    | 'scheduleName'
    | 'permittedActions'
    | 'activityCodes'
  >;

export type ScheduleActivityEntityBlockProps = Exclude<
  ScheduleActivityReferencePickerPlugin['EntityBlock'],
  undefined
>;

export type PaginatedResults<T> = {
  results: T[];
  pagination: { offset: number; limit: number; totalResults: number };
};

export type Assignee = { name: string; unsanitizedName: string; type?: string };

export type Activity = {
  id: string;
  scheduleId: string;
  sequentialId: number;
  uniqueId: string;
  name: string;
  parentId?: string;
  type?: ActivityType;
  progress?: number;
  completionPercentage: number;
  start: string;
  finish: string;
  actualStart?: string;
  actualFinish?: string;
  plannedStart?: string;
  plannedFinish?: string;
  startDateToDisplay: string;
  endDateToDisplay: string;
  assignees: { name: string; type: string }[];
  isCriticalPath?: boolean;
  durationDays?: number;
  remainingDurationDays?: number;
  wbsPath?: number[];
  wbsPathActivities?: Activity[];
  isWbs: boolean;
  fileActivityId?: string;
  activityCodes: ActivityCode[];
  scheduleName?: string;
  permittedActions?: string[];
};

export type ActivityRow = {
  id: string;
  activity: Activity;
  expanded: boolean;
  disabled: boolean;
  subRows: ActivityRow[];
};

export type ActivityCode = {
  name: string;
  value: string;
  value_description: string;
};

export enum ActivityType {
  Task = 'TASK',
  Milestone = 'MILESTONE',
  Wbs = 'PROJECT',
  Activity = 'ACITIVTY',
}

export enum ActivityStatus {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETE = 'COMPLETE',
}

export type ScheduleFilters = {
  scheduleId: string;
  searchExpression?: string;
  milestone?: boolean;
  critical?: boolean;
  statuses?: ActivityStatus[];
  resources?: string[];
  dateRange?: Date[];
  quickLook?: QuickLookFilters[];
  [key: string]: unknown;
};

export type Schedule = {
  id: string;
  name: string;
  type: string;
  dataDate: string;
  permittedActions: string[];
};

export enum QuickLookFilters {
  THIS_WEEK = 'THIS_WEEK',
  ONE_WEEK = 'ONE_WEEK',
  TWO_WEEKS = 'TWO_WEEKS',
  THREE_WEEKS = 'THREE_WEEKS',
  FOUR_WEEKS = 'FOUR_WEEKS',
  FIVE_WEEKS = 'FIVE_WEEKS',
  SIX_WEEKS = 'SIX_WEEKS',
}

export enum IsoWeekday {
  MONDAY = 1,
  TUESDAY,
  WEDNESDAY,
  THURSDAY,
  FRIDAY,
  SATURDAY,
  SUNDAY,
}

export type DropdownItem<T> = {
  label: string | ReactNode;
  value: T;
};

export type ActivityEntity = {
  id: string;
  activity: ScheduleActivityEntity;
};

export type ScheduleActivityReferencePickerPlugin = ReferencePickerPlugin<
  ActivityEntity,
  ScheduleFilters
>;

export type DropdownSingleSelectProps = {
  highlightedIndex?: number;
  inputValue?: string;
  isOpen?: boolean;
  selectedItem?: GenericItem | null;
  type: string;
};

export type DropdownMultiSelectProps = {
  activeIndex?: number;
  selectedItems?: GenericItem[];
  type: string;
};

export type DropdownRenderMenuType = ComponentProps<
  typeof Dropdown
>['renderMenu'];
